import React from 'react';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema, softwareAppSchema, faqSchema } from '../utils/structuredData';
import { CameraIcon, SparklesIcon, UploadIcon, PaletteIcon, ImageIcon, DownloadIcon, ArrowRightIcon, ExternalLinkIcon, CheckCircle2Icon, StarIcon, WandIcon, LayoutGridIcon, TrendingUpIcon, DollarSignIcon, RocketIcon } from 'lucide-react';
const SHOPIFY_URL = 'https://apps.shopify.com/ai-product-photoshoot-dfd';
function InstallButton({
  size = 'md',
  variant = 'solid'
}: {
  size?: 'md' | 'lg';
  variant?: 'solid' | 'white';
}) {
  const pad = size === 'lg' ? 'px-10 py-5 text-lg' : 'px-8 py-4 text-base';
  const color = variant === 'white' ? 'bg-white text-black hover:bg-gray-200 shadow-[0_0_30px_rgba(255,255,255,0.2)]' : 'bg-purple-600 text-white hover:bg-purple-500 shadow-[0_0_30px_rgba(147,51,234,0.3)]';
  return <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2 ${color} ${pad} rounded-full font-bold transition-all duration-300 hover:scale-105`} data-id="element-586">
      Install Free on Shopify <ExternalLinkIcon className="w-5 h-5" data-id="element-587" />
    </a>;
}
function StarRow() {
  return <div className="flex items-center gap-1" data-id="element-588">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-purple-400 fill-purple-400" data-id="element-589" />)}
    </div>;
}
function AppFrame({
  title,
  children
}: {
  title?: string;
  children: React.ReactNode;
}) {
  return <div className="glass-card border-white/10 p-2 rounded-2xl relative shadow-2xl shadow-black/50" data-id="element-590">
      <div className="bg-[#0A0A0A] rounded-xl overflow-hidden border border-white/5" data-id="element-591">
        <div className="h-10 border-b border-white/10 flex items-center px-4 gap-2 bg-white/5" data-id="element-592">
          <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-593" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-594" />
          <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-595" />
          {title && <span className="ml-3 text-xs text-gray-500 font-medium" data-id="element-596">
              {title}
            </span>}
        </div>
        <div className="p-6" data-id="element-597">{children}</div>
      </div>
    </div>;
}
interface FeatureBlock {
  eyebrow: string;
  title: string;
  desc: string;
  bullets: string[];
  mockup: React.ReactNode;
}
const FEATURE_BLOCKS: FeatureBlock[] = [{
  eyebrow: 'AI Scene Studio',
  title: 'Turn plain product shots into scroll-stopping scenes',
  desc: 'Upload any product photo and our AI drops it into a beautiful, realistic lifestyle scene, like marble countertops, sunlit studios, or cozy interiors, in seconds. No photographer, no studio, no props.',
  bullets: ['Realistic AI lifestyle scenes from a single product photo', 'Describe any scene in plain words, or pick a preset', 'Studio-quality results without a studio'],
  mockup: <div className="space-y-4" data-id="element-598">
        <div className="flex items-center gap-4" data-id="element-599">
          <div className="flex-1 bg-white/5 rounded-xl p-4 border border-white/10 text-center" data-id="element-600">
            <div className="aspect-square bg-white/10 rounded-lg mb-3 flex items-center justify-center" data-id="element-601">
              <CameraIcon className="w-10 h-10 text-gray-500" data-id="element-602" />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400" data-id="element-603">
              Original
            </span>
          </div>
          <div className="shrink-0 flex flex-col items-center" data-id="element-604">
            <SparklesIcon className="w-7 h-7 text-purple-400 animate-pulse mb-1" data-id="element-605" />
            <ArrowRightIcon className="w-5 h-5 text-gray-600" data-id="element-606" />
          </div>
          <div className="flex-1 bg-purple-500/10 rounded-xl p-4 border border-purple-500/30 text-center relative overflow-hidden" data-id="element-607">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent opacity-50" data-id="element-608" />
            <div className="aspect-square bg-gradient-to-br from-purple-900/60 to-black rounded-lg mb-3 flex items-center justify-center border border-purple-500/20 relative z-10" data-id="element-609">
              <ImageIcon className="w-10 h-10 text-purple-300" data-id="element-610" />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-purple-300 relative z-10" data-id="element-611">
              AI Lifestyle
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-purple-300 bg-purple-500/10 border border-purple-500/20 rounded-lg p-2.5" data-id="element-612">
          <WandIcon className="w-4 h-4" data-id="element-613" /> “Place on a sunlit marble kitchen
          counter”
        </div>
      </div>
}, {
  eyebrow: 'Preset Categories',
  title: '20+ ready-made scene templates',
  desc: 'Not sure where to start? Choose from a growing library of professionally designed scene presets tailored to your product category, including skincare, apparel, food, home goods and more.',
  bullets: ['20+ curated scene presets across product categories', 'One tap to generate on-brand imagery', 'Fresh templates added regularly'],
  mockup: <div className="space-y-3" data-id="element-614">
        <div className="flex items-center justify-between mb-1" data-id="element-615">
          <span className="text-sm font-semibold text-white" data-id="element-616">
            Scene Presets
          </span>
          <span className="text-xs text-purple-300" data-id="element-617">20+ themes</span>
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-618">
          {['Skincare', 'Apparel', 'Food', 'Home', 'Jewelry', 'Outdoor'].map(cat => <div key={cat} className="rounded-lg border border-white/10 bg-white/5 p-3 text-center hover:border-purple-500/30 transition-colors" data-id="element-619">
                <div className="aspect-square rounded-md bg-gradient-to-br from-purple-900/40 to-black mb-2 flex items-center justify-center" data-id="element-620">
                  <PaletteIcon className="w-5 h-5 text-purple-300" data-id="element-621" />
                </div>
                <span className="text-[11px] text-gray-300" data-id="element-622">{cat}</span>
              </div>)}
        </div>
      </div>
}, {
  eyebrow: 'One-Click Publishing',
  title: 'Straight to your store, no downloads, no re-uploads',
  desc: 'Love a result? Publish it directly to the product in your Shopify catalog with a single click. Your storefront updates instantly. No file juggling, no manual uploads.',
  bullets: ['Publish generated images right to your Shopify products', 'Pulls products directly from your catalog', 'Instant storefront updates'],
  mockup: <div className="space-y-3" data-id="element-623">
        <span className="text-sm font-semibold text-white" data-id="element-624">
          Publish to Catalog
        </span>
        {[{
      name: 'Hydrating Face Serum',
      status: 'Published'
    }, {
      name: 'Linen Summer Dress',
      status: 'Published'
    }, {
      name: 'Ceramic Coffee Mug',
      status: 'Publishing…'
    }].map(p => <div key={p.name} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3" data-id="element-625">
            <div className="w-9 h-9 rounded-md bg-purple-500/10 border border-purple-500/20 flex items-center justify-center shrink-0" data-id="element-626">
              <UploadIcon className="w-4 h-4 text-purple-300" data-id="element-627" />
            </div>
            <span className="flex-1 text-xs text-gray-300 truncate" data-id="element-628">
              {p.name}
            </span>
            <span className={`text-[11px] ${p.status === 'Published' ? 'text-purple-300' : 'text-gray-500'}`} data-id="element-629">
              {p.status}
            </span>
          </div>)}
      </div>
}, {
  eyebrow: 'Gallery & Export',
  title: 'Every asset saved, ready to reuse',
  desc: 'All your generated images live in a permanent in-app gallery, so you can reuse them for ads, email, and social anytime. Export in high resolution for any channel.',
  bullets: ['Permanent gallery of every generated image', 'High-resolution export for ads & social', 'Reuse assets across all your marketing'],
  mockup: <div className="space-y-3" data-id="element-630">
        <div className="flex items-center justify-between" data-id="element-631">
          <span className="text-sm font-semibold text-white" data-id="element-632">Your Gallery</span>
          <span className="text-xs text-purple-300 flex items-center gap-1" data-id="element-633">
            <DownloadIcon className="w-3.5 h-3.5" data-id="element-634" /> Hi-res export
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2" data-id="element-635">
          {Array.from({
        length: 8
      }).map((_, i) => <div key={i} className="aspect-square rounded-md bg-gradient-to-br from-purple-900/50 to-black border border-purple-500/20 flex items-center justify-center" data-id="element-636">
              <ImageIcon className="w-4 h-4 text-purple-300/70" data-id="element-637" />
            </div>)}
        </div>
      </div>
}];
const STATS = [{
  value: '20+',
  label: 'Scene presets'
}, {
  value: '< 30s',
  label: 'Per image'
}, {
  value: '$0',
  label: 'To get started'
}, {
  value: '4.9★',
  label: 'Merchant rating'
}];
const OUTCOMES = [{
  icon: <TrendingUpIcon className="w-7 h-7 text-purple-400" data-id="element-638" />,
  title: 'Convert more',
  desc: 'Lifestyle imagery helps shoppers picture the product in their lives, and that drives more add-to-carts.'
}, {
  icon: <DollarSignIcon className="w-7 h-7 text-purple-400" data-id="element-639" />,
  title: 'Save thousands',
  desc: 'Skip expensive photographers, studios, and prop rentals. Get studio-grade visuals for free.'
}, {
  icon: <RocketIcon className="w-7 h-7 text-purple-400" data-id="element-640" />,
  title: 'Ship faster',
  desc: 'Launch products and campaigns the same day, with no waiting weeks for a photoshoot.'
}];
const TESTIMONIALS = [{
  quote: 'We replaced an entire photoshoot budget with this app. Our product pages have never looked better and conversions are up.',
  name: 'Megan T.',
  role: 'DTC Skincare Brand'
}, {
  quote: 'I generated a full season of lifestyle shots in an afternoon. The presets are genuinely good and publishing to Shopify is instant.',
  name: 'Carlos V.',
  role: 'Apparel Store Owner'
}, {
  quote: 'The before/after is night and day. Plain packshots turned into scroll-stopping ads. Free plan got us hooked.',
  name: 'Aisha K.',
  role: 'Home Goods Merchant'
}];
const FAQS = [{
  q: 'Do I need design or photography skills?',
  a: 'Not at all. Pick a product, choose a preset or describe a scene in plain words, and the AI does the rest. Publishing to your store is one click.'
}, {
  q: 'Is it really free?',
  a: 'Yes. There’s a free plan with no credit card required. You can generate images, build your gallery, and publish to your store to see the value first.'
}, {
  q: 'Will the images match my product accurately?',
  a: 'The AI places your actual product photo into the new scene, so your product stays true while the background and styling are transformed.'
}, {
  q: 'Where can I use the generated images?',
  a: 'Anywhere, including product pages, social media, email, and paid ads. Export in high resolution for any channel.'
}];
export function AIPhotoshootPage() {
  usePageSEO({
    canonical: '/ai-photoshoot',
    keywords: 'AI product photography, Shopify AI app, AI photoshoot Shopify, product background generator, ecommerce lifestyle images',
    ogType: 'product',
    jsonLd: [breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Apps',
      path: '/apps'
    }, {
      name: 'AI Photoshoot',
      path: '/ai-photoshoot'
    }]), {
      ...softwareAppSchema({
        name: 'AI Product Photoshoot',
        description: 'AI Product Photoshoot turns plain Shopify product photos into high-converting lifestyle images in seconds.',
        path: '/ai-photoshoot',
        category: 'BusinessApplication'
      }),
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '86'
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      }
    }, faqSchema(FAQS.map(f => ({
      question: f.q,
      answer: f.a
    })))],
    title: 'AI Product Photoshoot for Shopify | Design Flow Digitals',
    description: 'AI Product Photoshoot turns plain Shopify product photos into high-converting lifestyle images in seconds. 20+ scene presets, one-click publishing, free plan. Install today.'
  });
  return <div className="w-full selection:bg-purple-500/30 selection:text-white" data-id="element-641">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center pt-32 pb-20 overflow-hidden" data-id="element-642">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-black pointer-events-none" data-id="element-643" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" data-id="element-644" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-645">
          <div data-id="element-646">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-purple-500/30 bg-purple-500/10" data-id="element-647">
              <CameraIcon className="w-4 h-4 text-purple-400" data-id="element-648" />
              <span className="text-sm font-medium text-purple-300" data-id="element-649">
                Live on Shopify App Store
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-id="element-650">
              Studio-quality product photos, powered by AI
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed" data-id="element-651">
              Transform plain Shopify product shots into high-converting
              lifestyle scenes in seconds, with no photographer, no studio, no
              props.
            </p>
            <div className="flex items-center gap-4 mb-10" data-id="element-652">
              <StarRow data-id="element-653" />
              <span className="text-sm text-gray-400" data-id="element-654">
                4.9 rating · Free plan available
              </span>
            </div>
            <div className="flex flex-wrap gap-4" data-id="element-655">
              <InstallButton size="lg" variant="white" data-id="element-656" />
              <a href="#how-it-works" className="px-8 py-4 rounded-full font-bold text-lg text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-657">
                See how it works
              </a>
            </div>
          </div>

          {/* Hero Mockup */}
          <div className="relative" data-id="element-658">
            <div className="absolute inset-0 bg-purple-500/20 blur-[100px] rounded-full" data-id="element-659" />
            <div className="relative z-10" data-id="element-660">
              <AppFrame title="AI Photoshoot Studio" data-id="element-661">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-662">
                  <div className="flex-1 w-full bg-white/5 rounded-xl p-4 border border-white/10 text-center" data-id="element-663">
                    <div className="aspect-square bg-white/10 rounded-lg mb-4 flex items-center justify-center" data-id="element-664">
                      <CameraIcon className="w-12 h-12 text-gray-500" data-id="element-665" />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-666">
                      Before (Plain)
                    </span>
                  </div>
                  <div className="shrink-0 flex flex-col items-center" data-id="element-667">
                    <SparklesIcon className="w-8 h-8 text-purple-500 mb-2 animate-pulse" data-id="element-668" />
                    <ArrowRightIcon className="w-6 h-6 text-gray-500 hidden md:block" data-id="element-669" />
                  </div>
                  <div className="flex-1 w-full bg-purple-500/10 rounded-xl p-4 border border-purple-500/30 text-center relative overflow-hidden" data-id="element-670">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent opacity-50" data-id="element-671" />
                    <div className="aspect-square bg-gradient-to-br from-purple-900/50 to-black rounded-lg mb-4 flex items-center justify-center border border-purple-500/20 relative z-10" data-id="element-672">
                      <ImageIcon className="w-12 h-12 text-purple-300" data-id="element-673" />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-purple-300 relative z-10" data-id="element-674">
                      After (Lifestyle)
                    </span>
                  </div>
                </div>
              </AppFrame>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="border-y border-white/5 bg-[#050505]" data-id="element-675">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-676">
          {STATS.map(s => <div key={s.label} className="text-center" data-id="element-677">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-678">
                {s.value}
              </p>
              <p className="text-sm text-gray-500" data-id="element-679">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="section-padding bg-black" data-id="element-680">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-681">
          <h2 className="text-4xl font-bold mb-6" data-id="element-682">
            Better visuals. More sales. Less spend.
          </h2>
          <p className="text-xl text-gray-400" data-id="element-683">
            Great product imagery is the fastest lever for conversion, and now
            it costs you nothing to start.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-684">
          {OUTCOMES.map(o => <div key={o.title} className="glass-card p-8 border-white/10 hover:border-purple-500/30 transition-colors" data-id="element-685">
              <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-6 border border-purple-500/20" data-id="element-686">
                {o.icon}
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-687">{o.title}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-688">{o.desc}</p>
            </div>)}
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section-padding bg-[#020202] border-y border-white/5" data-id="element-689">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-690">
          <h2 className="text-4xl font-bold mb-6" data-id="element-691">How It Works</h2>
          <p className="text-xl text-gray-400" data-id="element-692">
            Generate stunning imagery in three simple steps.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-693">
          {[{
          step: '01',
          title: 'Select Your Product',
          desc: 'Choose any product directly from your Shopify catalog.'
        }, {
          step: '02',
          title: 'Describe Your Scene',
          desc: 'Use custom prompts or choose from 20+ pre-set themes.'
        }, {
          step: '03',
          title: 'Generate & Publish',
          desc: 'AI creates stunning lifestyle images. Publish directly to your store.'
        }].map((item, i) => <div key={i} className="glass-card p-8 border-white/10 relative overflow-hidden group" data-id="element-694">
              <div className="absolute top-0 left-0 w-full h-1 bg-purple-500/50 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" data-id="element-695" />
              <div className="text-5xl font-bold text-purple-500/20 mb-6" data-id="element-696">
                {item.step}
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-697">
                {item.title}
              </h3>
              <p className="text-gray-400" data-id="element-698">{item.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Detailed Feature Blocks with mockups */}
      <section className="bg-black border-b border-white/5" data-id="element-699">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 space-y-28" data-id="element-700">
          {FEATURE_BLOCKS.map((f, i) => <div key={f.title} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-701">
              <div className={i % 2 === 1 ? 'lg:order-2' : ''} data-id="element-702">
                <p className="text-sm font-bold text-purple-400 uppercase tracking-widest mb-4" data-id="element-703">
                  {f.eyebrow}
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight" data-id="element-704">
                  {f.title}
                </h3>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-705">
                  {f.desc}
                </p>
                <ul className="space-y-4" data-id="element-706">
                  {f.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-300" data-id="element-707">
                      <CheckCircle2Icon className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" data-id="element-708" />
                      <span data-id="element-709">{b}</span>
                    </li>)}
                </ul>
              </div>
              <div className={i % 2 === 1 ? 'lg:order-1' : ''} data-id="element-710">
                <AppFrame data-id="element-711">{f.mockup}</AppFrame>
              </div>
            </div>)}
        </div>
      </section>

      {/* Key Features grid */}
      <section className="section-padding bg-black" data-id="element-712">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-713">
          <h2 className="text-4xl font-bold mb-6" data-id="element-714">Everything you get</h2>
          <p className="text-xl text-gray-400" data-id="element-715">
            A complete product-photography studio inside your Shopify admin.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-716">
          {[{
          icon: <SparklesIcon className="w-6 h-6 text-purple-400" data-id="element-717" />,
          title: 'AI-Powered Generation',
          desc: 'Advanced AI creates realistic lifestyle imagery from your product photos.'
        }, {
          icon: <UploadIcon className="w-6 h-6 text-purple-400" data-id="element-718" />,
          title: 'Direct Publishing',
          desc: 'Publish generated images directly to your Shopify products with one click.'
        }, {
          icon: <PaletteIcon className="w-6 h-6 text-purple-400" data-id="element-719" />,
          title: 'Custom Scenes',
          desc: 'Describe any scene you want or use our 20+ pre-set product categories.'
        }, {
          icon: <LayoutGridIcon className="w-6 h-6 text-purple-400" data-id="element-720" />,
          title: 'Image Gallery',
          desc: 'All generated assets are stored permanently in-app for repeat use.'
        }, {
          icon: <DownloadIcon className="w-6 h-6 text-purple-400" data-id="element-721" />,
          title: 'High Resolution Export',
          desc: 'Export files for product pages, social media, and ad campaigns.'
        }, {
          icon: <CheckCircle2Icon className="w-6 h-6 text-purple-400" data-id="element-722" />,
          title: 'Free to Start',
          desc: 'Begin on the free plan with no credit card required.'
        }].map((feature, i) => <div key={i} className="glass-card p-8 border-white/10 hover:border-purple-500/30 transition-colors" data-id="element-723">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-6 border border-purple-500/20" data-id="element-724">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-725">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-726">
                {feature.desc}
              </p>
            </div>)}
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-padding bg-[#050505] border-y border-white/5" data-id="element-727">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-728">
          <h2 className="text-4xl font-bold mb-6" data-id="element-729">Perfect For</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-730">
          {['Product Pages', 'Social Media Content', 'Ad Campaigns', 'Seasonal Promotions'].map((useCase, i) => <div key={i} className="glass-card p-6 border-white/10 flex items-center gap-4" data-id="element-731">
              <CheckCircle2Icon className="w-6 h-6 text-purple-500 shrink-0" data-id="element-732" />
              <span className="text-lg font-medium text-white" data-id="element-733">{useCase}</span>
            </div>)}
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-black" data-id="element-734">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-735">
          <h2 className="text-4xl font-bold mb-6" data-id="element-736">
            Merchants love the results
          </h2>
          <p className="text-xl text-gray-400" data-id="element-737">
            Join Shopify stores creating scroll-stopping visuals without a
            studio.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-738">
          {TESTIMONIALS.map(t => <div key={t.name} className="glass-card p-8 border-white/10" data-id="element-739">
              <StarRow data-id="element-740" />
              <p className="text-gray-300 leading-relaxed my-6" data-id="element-741">“{t.quote}”</p>
              <div data-id="element-742">
                <p className="font-bold text-white" data-id="element-743">{t.name}</p>
                <p className="text-sm text-gray-500" data-id="element-744">{t.role}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* Pricing */}
      <section className="section-padding bg-[#020202] border-t border-white/5" data-id="element-745">
        <div className="max-w-md mx-auto" data-id="element-746">
          <div className="glass-card p-10 border-purple-500/30 bg-gradient-to-b from-purple-900/10 to-transparent text-center relative overflow-hidden" data-id="element-747">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-purple-400 to-purple-600" data-id="element-748" />
            <h3 className="text-3xl font-bold text-white mb-4" data-id="element-749">
              Free Plan Available
            </h3>
            <p className="text-gray-400 mb-8" data-id="element-750">
              Get started with our free plan. No credit card required.
            </p>
            <ul className="space-y-4 mb-10 text-left" data-id="element-751">
              {['AI image generation', 'Product catalog integration', 'Permanent image gallery', 'Direct publishing to store'].map((f, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-752">
                  <CheckCircle2Icon className="w-5 h-5 text-purple-400 shrink-0" data-id="element-753" />{' '}
                  {f}
                </li>)}
            </ul>
            <InstallButton data-id="element-754" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-black border-t border-white/5" data-id="element-755">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-756">
          <h2 className="text-4xl font-bold mb-6" data-id="element-757">
            Frequently asked questions
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-5" data-id="element-758">
          {FAQS.map(f => <div key={f.q} className="glass-card p-6 border-white/10" data-id="element-759">
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-760">{f.q}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-761">{f.a}</p>
            </div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-purple-900/20" data-id="element-762">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-763">
          Ready to transform your product photography?
        </h2>
        <p className="text-xl text-gray-400 mb-4 max-w-2xl mx-auto" data-id="element-764">
          Install AI Product Photoshoot and create your first lifestyle scene in
          under a minute.
        </p>
        <div className="flex items-center justify-center gap-3 mb-10" data-id="element-765">
          <StarRow data-id="element-766" />
          <span className="text-sm text-gray-400" data-id="element-767">
            4.9 rating · Free plan · No credit card
          </span>
        </div>
        <InstallButton size="lg" data-id="element-768" />

        {/* App Details Footer */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap justify-center gap-8 text-sm text-gray-500 max-w-3xl mx-auto" data-id="element-769">
          <p data-id="element-770">
            Developer: <span className="text-gray-300" data-id="element-771">Pankaj Maurya</span>
          </p>
          <p data-id="element-772">
            Works with: <span className="text-gray-300" data-id="element-773">Shopify Admin</span>
          </p>
          <p data-id="element-774">
            Language: <span className="text-gray-300" data-id="element-775">English</span>
          </p>
        </div>
      </section>
    </div>;
}